'use client';

import React, { JSX, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { AppDispatch } from '@/store/store';
import { CheckoutShippingMethodLocation } from '@/types/checkout/CheckoutShippingMethodLocation';

import { CheckoutStepInputComponentProps } from '@/types/checkout/CheckoutStepInputComponent';

import { checkoutFetchShippingMethods } from '@/actions/checkout';
import { storeIntegrationStampsAddressVerification } from '@/actions/store';
import { usersUpdateAddress } from '@/actions/user';

import { CHECKOUT_STEP_SHIPPING_ADDRESS } from '@/constants/checkout';
import {
	TX_CHECKOUT_ADDRESS_VERIFICATION,
	TX_CHECKOUT_ADDRESS_VERIFICATION_ADDRESS_ENTERED,
	TX_CHECKOUT_ADDRESS_VERIFICATION_DOES_NOT_MATCH,
	TX_CHECKOUT_PICKUP_DISABLED_NOTICE,
	TX_PICKUP,
	TX_SAVE,
	TX_SHIPPING_METHOD,
	TX_SHIPPING_METHODS,
} from '@/constants/strings';

import useCart from '@/hooks/useCart';
import useCheckout from '@/hooks/useCheckout';
import usePrevious from '@/hooks/usePrevious';
import useStore from '@/hooks/useStore';

import Address from '@/models/geographies/Address';
import Location from '@/models/locations/Location';
import ShippingMethod from '@/models/shipping/ShippingMethod';

import { addShippingInfoEvent } from '@/utils/analytics';

import LoadingIcon from '@/components/Icons/LoadingIcon';
import Modal from '@/components/Popups/Modal';
import Radio from '@/components/Input/Radio';
import ShippingMethodLocationInput from '@/components/Checkout/blocks/steps/shipping-method/ShippingMethodLocationInput';

import stepStyles from '../_styles/CheckoutStepComponent.module.scss';
import styles from './_styles/CheckoutStepShippingMethodInput.module.scss';


// Prop types
// Imported from src/types/checkout/...

export const CheckoutStepShippingMethodInput = (props: CheckoutStepInputComponentProps): JSX.Element => {
  
	// Dispatch
	const dispatch = useDispatch<AppDispatch>();

	// Constants
	// None yet

	// Props
	const {
		config,
		setStepData,
	} = props;

	// Local state
	const [ addressModalOpen, setAddressModalOpen ] = useState<boolean>(false);
	const [ addressModalCandidates, setAddressModalCandidates ] = useState<Address[]>([]);
	const [ selectedCandidateIndex, setSelectedCandidateIndex ] = useState<number>(-1);

	const [ selectedMethods, setSelectedMethods ] = useState<CheckoutShippingMethodLocation[]>([]);
	const [ shippingMethods, setShippingMethods ] = useState<ShippingMethod[]>([]);
	const [ shippingMethodsLoading, setShippingMethodsLoading ] = useState<boolean>(true);

	// Checkout session
	const { checkoutSession } = useCheckout();
	
	// Store
	const { store } = useStore();
	
	// Cart
	const { activeCart } = useCart();

	// Previous subtotal
	const prevSubtotal = usePrevious(activeCart?.subTotal);

	// Refs
	const fetchControllerRef = React.useRef<AbortController | null>(null);
	const shippingAddressRef = React.useRef<Address | null>(null);

	// Internationalization
	const { t } = useTranslation();

	// Derived constants
	const applicableShippingMethods = useMemo((): ShippingMethod[] => {
			
		if(!activeCart || !shippingMethods.length) {
			return [];
		}
		
		// No location-based filtering here
		
		const methodsResp: ShippingMethod[] = [];
		for(const ship of shippingMethods) {
			if(ship.cost < 0) {
				continue;
			}
			if(!isNaN(ship.minPurchase) && activeCart.subTotal >= ship.minPurchase) {
				methodsResp.push(ship);
			}
		}
		return methodsResp;

	}, [ activeCart, shippingMethods ]);

	const disablePickup = useMemo((): boolean => {
		if(!store || !activeCart) {
			return false;
		}

		if(store.multiLocation && activeCart.locations.length > 1) {
			return applicableShippingMethods.some(method => method.isPickup);
		}

		// Default to false
		return false;

	}, [ store, activeCart, applicableShippingMethods ]);

	// Methods
	const fetchShippingMethods = useCallback(async () => {

		if(!checkoutSession?.shippingAddress?.publicUuid) {
			return;
		}

		if(fetchControllerRef.current) {
			fetchControllerRef.current.abort();
		}
		const controller = new AbortController();
		fetchControllerRef.current = controller;

		try {

			setShippingMethodsLoading(true);

			const methodsResp = await dispatch(checkoutFetchShippingMethods(checkoutSession.shippingAddress.publicUuid, controller.signal));

			setShippingMethods(methodsResp);			
			setShippingMethodsLoading(false);

		} catch(err) {
			if(controller.signal.aborted) { return null; }
			setShippingMethodsLoading(false);
			console.error(err);
		}
	}, [ dispatch, checkoutSession?.shippingAddress?.publicUuid ]);

	const validateShippingMethods = useCallback(async () => {
		
		let modified = false;
		const filteredMethods: CheckoutShippingMethodLocation[] = [];
		for(const sm of selectedMethods) {
			const methodValid = applicableShippingMethods.some(appSm => appSm.publicUuid === sm.shippingMethod?.publicUuid);
			
			if(methodValid) {
				filteredMethods.push(sm);
			} else {
				modified = true;
			}
		}

		setSelectedMethods(filteredMethods);
		setStepData(config, {
			shippingMethods: filteredMethods, 
			requiredCount: activeCart?.locations.length || 1,
		});

	}, [ setStepData, shippingMethods, applicableShippingMethods, activeCart?.locations.length ]);

	const fetchAddressValidation = async (method: ShippingMethod) => {

		try {

			const shippingAddress = checkoutSession?.shippingAddress;
			if(!shippingAddress?.publicUuid || !method.publicUuid) {
				return null;
			}

			const validationResp = await dispatch(storeIntegrationStampsAddressVerification(method.publicUuid, shippingAddress.publicUuid));

			const success_codes = [ 'V100' ];

			const resp = validationResp[0];

			if(resp?.validation_results?.result_code && !success_codes.includes(resp.validation_results.result_code)) {

				if(!resp.candidate_addresses || !resp.candidate_addresses.length) {
					return null;
				}

				// Convert addresses to models
				const candidates = [];
				for(const ca of resp.candidate_addresses) {
					candidates.push(new Address(Object.assign({}, ca, {
						publicUuid: shippingAddress.publicUuid, 
						placeId: shippingAddress.placeId,
						firstName: shippingAddress.firstName,
						lastName: shippingAddress.lastName,
						country: shippingAddress.country,
						phone: shippingAddress.phone,
					})));
				}

				setAddressModalOpen(true);
				setAddressModalCandidates(candidates);
				setSelectedCandidateIndex(-1);
			}

		} catch(err) {
			console.error(err);
			return null;
		}
	}

	const updateShippingMethods = useCallback((method: ShippingMethod, location: Location) => {

		const newMethods = selectedMethods.filter((sm) => sm.location && sm.location?.publicUuid !== location.publicUuid);
		newMethods.push({
			location: location,
			shippingMethod: method,
		});
		setSelectedMethods(newMethods);

		if(method.integration?.isEnabled && method.integration?.isStamps) {
			fetchAddressValidation(method);
		}

		// Fire add_shipping_info analytics event when user selects shipping method
		addShippingInfoEvent(activeCart, method, {});
			
		setStepData(config, {
			shippingMethods: newMethods, 
			requiredCount: activeCart?.locations.length || 1,
		});
				
	}, [ selectedMethods, setStepData, config, activeCart ]);

	const closeAddressModal = useCallback(() => {
		setAddressModalOpen(false);
		setAddressModalCandidates([]);
		setSelectedCandidateIndex(-1);
	}, []);

	const updateAddress = async () => {
		try {

			const updatedAddress = addressModalCandidates[selectedCandidateIndex];
			if(!updatedAddress) {
				return false;
			}

			const updateAddressResp = await dispatch(usersUpdateAddress(updatedAddress.getApiData()));

			setStepData(CHECKOUT_STEP_SHIPPING_ADDRESS, {
				shippingAddress: updateAddressResp, 
				requiredCount: activeCart?.locations.length || 1,
			});
			return true;

		} catch(err) {
			console.error(err);
			return false;
		}
	};

	const selectCandidateAddress = (idx: number) => {
		setSelectedCandidateIndex(idx);
	}

	const getSelectedMethodForLocation = (location: Location): ShippingMethod | null => {
		const smLocation = selectedMethods.find(sm => sm.location?.publicUuid === location.publicUuid);
		return smLocation?.shippingMethod || null;
	};

	// Effects
	useEffect(() => {
		if(selectedMethods.length === 0 && checkoutSession?.shippingMethods?.length) {
			setSelectedMethods(checkoutSession?.shippingMethods);
		}
	}, [ checkoutSession ]);

	useEffect(() => {
		validateShippingMethods();
	}, [ validateShippingMethods ]);

	useEffect(() => {
		fetchShippingMethods();
	}, [ fetchShippingMethods ]);

	useEffect(() => {
		if(checkoutSession?.shippingAddress?.publicUuid && checkoutSession.shippingAddress?.publicUuid !== shippingAddressRef.current?.publicUuid) {
			if(!shippingAddressRef.current) {
				fetchShippingMethods();
			}
			shippingAddressRef.current = checkoutSession.shippingAddress?.publicUuid 
				? checkoutSession.shippingAddress 
				: null;
		}
	}, [ checkoutSession ]);

	useEffect(() => {
		if(prevSubtotal && activeCart?.subTotal && activeCart.subTotal !== prevSubtotal) {
			setStepData(config, {
				shippingMethods: [], 
				requiredCount: activeCart?.locations.length || 1,
			});
			setSelectedMethods([]);
			fetchShippingMethods();
		}
	}, [ activeCart?.subTotal, activeCart?.locations.length, prevSubtotal, fetchShippingMethods ]);


	// The whole ".multiLocation === true" check here is a temporary measure
	// Can be removed about a week after SSR rollout is complete
	// Only because not all carts will have location data immediately after SSR rollout

	// Render
	return (
		<div className={`${styles.CheckoutStepShippingMethodInput} ${stepStyles.CheckoutStepComponent}`}>
			<div className={styles.shippingMethodWrapper}>
				
				<div className={stepStyles.checkoutFormSubheader}>{t((activeCart?.locations?.length || 0) > 1 ? TX_SHIPPING_METHODS : TX_SHIPPING_METHOD)}</div>
				{shippingMethodsLoading ?
					<div className={styles.loadingWrapper}>
						<LoadingIcon />
					</div> :
					<>	
						{disablePickup && (
							<div className={styles.pickupNoticeWrapper}>
								<div className={styles.pickupTitle}>
									{t(TX_PICKUP)}
								</div>
								<div className={styles.pickupNotice}>
									{t(TX_CHECKOUT_PICKUP_DISABLED_NOTICE)}
								</div>
							</div>
						)}

						{(store && store.multiLocation === true) && (
							<>
								{activeCart?.locations.map(((loc, i) => (
									<ShippingMethodLocationInput
										key={loc.publicUuid || `loc-${i}`}
										disablePickup={!!disablePickup}
										location={loc}
										selectedMethod={getSelectedMethodForLocation(loc)}
										shippingMethods={shippingMethods}
										onSelect={(method) => updateShippingMethods(method, loc)} />
								)))}
							</>
						)}
						{(store && store.multiLocation === false && store.defaultLocation) && (
							<ShippingMethodLocationInput
								location={store.defaultLocation}
								selectedMethod={getSelectedMethodForLocation(store.defaultLocation)}
								shippingMethods={shippingMethods}
								onSelect={(method) => updateShippingMethods(method, store.defaultLocation!)} />
						)}
					</>
				}
			</div>
			<Modal
				title={TX_CHECKOUT_ADDRESS_VERIFICATION}
				open={addressModalOpen && checkoutSession?.shippingAddress !== null}
				confirmValue={TX_SAVE}
				closeMethod={closeAddressModal}
				onConfirm={updateAddress}>

				<div className={styles.addressValidationWrapper}>
					<div className={styles.addressValidation}>
						<div className={styles.avP}>{t(TX_CHECKOUT_ADDRESS_VERIFICATION_ADDRESS_ENTERED)}</div>
						<div 
							className={styles.addressBlock}
							dangerouslySetInnerHTML={{ 
								__html: checkoutSession?.shippingAddress?.format({
									addTags: true 
								}) || '<></>'
							}} />
						<div className={styles.avP}>{t(TX_CHECKOUT_ADDRESS_VERIFICATION_DOES_NOT_MATCH)}</div>
						<div className={styles.addressForm}>
							<div className={styles.formLiner}>
								{!!(addressModalCandidates && addressModalCandidates.length) && (
									<>
										{addressModalCandidates.map((addr, k) => {

											if(k > 5) {
												return null;
											}

											return <div key={k} className={`${styles.candidateEntry} ${selectedCandidateIndex === k ? styles.selected : ''}`}>
												<div className={styles.candidateInput}>
													<div className={styles.radioWrapper}>
														<Radio
															id={`candidate-option-${k}`}
															className={styles.candidateRadio}
															name='candidate-address'
															adminTheme={false}
															disabled={false}
															value={k}
															checked={selectedCandidateIndex === k}
															onChange={() => selectCandidateAddress(k)} />
													</div>
												</div>
												<div className={styles.candidateValue}>
													<label htmlFor={`candidate-option-${k}`}>
														<div dangerouslySetInnerHTML={{ __html: addr.format({ addTags: true }) }} />
													</label>
												</div>
											</div>
										})}
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default CheckoutStepShippingMethodInput;




